// src/components/Header.jsx — v13 FINAL
// Scroll auto-hide solo reaparece al tope. Iconos Lucide delgados.
// Flechas oscuras en light mode. Pestaña Planes normalizada.
import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import InstallPWA from './InstallPWA';
import UserStatusDropdown from './UserStatusDropdown';
import OmniWidgetV2 from './student/OmniWidgetV2/OmniWidgetV2';
import { useSubscriptionStatus } from './guards/SubscriptionGuard';
// Quien puede entrar al centro de mando lo decide UN solo fichero, el mismo que
// usa la guardia de la ruta. Copiar aqui la condicion garantiza que algun dia
// el boton se enseñe a quien la pantalla luego rechaza.
import {
  elDeLaSesionPuedeEntrar,
  RUTA_DEL_CENTRO_DE_MANDO,
} from './admin/puertaDelCentroDeMando';
import './Header.css';

import {
  Dumbbell,
  GraduationCap,
  Gamepad2,
  Brain,
  BarChart3,
  BookOpen,
  Zap,
  MessageCircle,
  Briefcase,
  CalendarCheck,
  ShieldCheck,
  Bot
} from 'lucide-react';

function Header({ forceDark }) {
  // ── ESTA CABECERA RESERVA SITIO EN EL BODY, ASI QUE TIENE QUE DECIRLO ────
  // En movil la cabecera es `position: fixed`, y el `body` lleva un relleno de
  // 58px arriba y ~64 abajo para que el contenido no quede debajo de ella.
  //
  // El problema es que ese relleno estaba puesto por ANCHO DE PANTALLA, y esta
  // cabecera se monta POR PANTALLA — en una leccion no existe. Resultado medido
  // en un telefono: el modo IA empezaba en y=58 con `height: 100dvh`, asi que
  // 58px se salian por abajo, y con `overflow: hidden` no habia forma de
  // llegar a ellos. El alumno veia un hueco muerto arriba y el contenido
  // cortado abajo.
  //
  // Ahora el relleno depende de que la cabecera EXISTA, que es lo que de
  // verdad lo justifica.
  useEffect(() => {
    document.body.classList.add('con-cabecera-fija');
    return () => document.body.classList.remove('con-cabecera-fija');
  }, []);

  // ── QUIEN VE EL CHAT ────────────────────────────────────────────────────
  // El asistente gasta IA de la empresa, así que el botón solo existe para
  // quien tiene acceso. `overdue` (prueba agotada, plan vencido, sin plan,
  // baja de su empresa) y `paused` (suspendido) se quedan sin él: ésos ya
  // tienen su salida por WhatsApp en la pared de reactivación y en el muro.
  // Quien se quedó sin TOKENS sí lo conserva: es un cliente al corriente y el
  // asistente sabe atenderle sin gastar un céntimo de IA.
  const { status: estadoPlan, loading: cargandoPlan } = useSubscriptionStatus();
  const conChat = !cargandoPlan && estadoPlan !== 'overdue' && estadoPlan !== 'paused';

  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
  const [collapsed, setCollapsed] = useState(false);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const scrollRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user'));

  // ── LA PUERTA DEL PROFESOR ───────────────────────────────────────────────
  // El enlace solo se pinta si aqui consta que es profesor, pero eso NO es la
  // seguridad: el `user_type` de `localStorage` se edita en dos clics. Quien
  // decide es el servidor —`/api/teacher` contesta 403 a cualquier otro— y
  // ademas `TeacherRoute` confirma contra el antes de abrir la pantalla.
  // Esto solo evita enseñarle una puerta a quien no la puede abrir.
  const esProfesor = user?.user_type === 'teacher';

  // ── LA PUERTA DEL CENTRO DE MANDO ────────────────────────────────────────
  // Rob, 24-09-2026: «si entro como administrador no tengo un boton de ir al
  // command center». Mismo criterio que el enlace del profesor: esto solo evita
  // enseñar una puerta a quien no la puede abrir. Lo que de verdad la cierra es
  // el servidor, que contesta 403 a todo `/api/admin/*` que no venga de un
  // administrador. Ver `admin/puertaDelCentroDeMando.js`.
  const esElJefe = elDeLaSesionPuedeEntrar();
  const navigate = useNavigate();
  const effectiveTheme = forceDark ? 'dark' : theme;

  /* ── Flechas de scroll horizontal ── */
  const checkArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 8);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkArrows();
    el.addEventListener('scroll', checkArrows, { passive: true });
    window.addEventListener('resize', checkArrows);
    return () => {
      el.removeEventListener('scroll', checkArrows);
      window.removeEventListener('resize', checkArrows);
    };
  }, [checkArrows]);

  const ignoreScroll = useRef(false);

  /* ── Smart scroll auto-hide ── */
  useEffect(() => {
    const SHOW_AT_TOP = 10;
    const HIDE_THRESHOLD = 60;
    let rafId = null;

    // ── SI LA PAGINA NO SE DESPLAZA, LA CABECERA NO SE ESCONDE ──────────────
    // Esta cabecera se esconde para dejar sitio MIENTRAS SE BAJA por la pagina.
    // En una pantalla donde todo cabe, esconderla no gana nada y cuesta la
    // navegacion entera: no hay pagina que subir para recuperarla.
    //
    // Rob lo vio en el coach, que ahora ocupa exactamente la pantalla y no se
    // desplaza. Y el fallo estaba a la vista aqui debajo: el `scroll` se
    // escucha en el documento CON CAPTURA, asi que llegan tambien los de
    // cualquier caja de dentro, y si la pagina esta a cero se toma el
    // `scrollTop` de esa caja como si fuera el de la pagina. Bajar la
    // conversacion 60px escondia el menu, y solo volvia subiendo la
    // conversacion otra vez. El alumno no tiene por que atar esas dos cosas.
    //
    // Y no es solo el coach: en CUALQUIER pantalla que no se desplace,
    // esconderla deja una banda muerta, porque el relleno que el `body` reserva
    // para ella se queda donde estaba.
    //
    // Se mide en el momento y no al montar: el teclado del movil, un giro o un
    // mensaje nuevo cambian esto en caliente.
    const laPaginaSeDesplaza = () => {
      const d = document.documentElement;
      return (d.scrollHeight - d.clientHeight) > HIDE_THRESHOLD;
    };

    const process = (currentY) => {
      if (ignoreScroll.current) {
        rafId = null;
        return;
      }

      if (!laPaginaSeDesplaza()) {
        setCollapsed(false);
        rafId = null;
        return;
      }

      if (currentY <= SHOW_AT_TOP) {
        setCollapsed(false);
      } else if (currentY > HIDE_THRESHOLD) {
        setCollapsed(true);
      }
      rafId = null;
    };

    const onScroll = (e) => {
      if (rafId !== null) return;
      let y = window.scrollY ?? window.pageYOffset ?? 0;
      if (y === 0 && e?.target && typeof e.target.scrollTop === 'number') {
        y = e.target.scrollTop;
      }
      if (y === 0) {
        y = document.documentElement.scrollTop || document.body.scrollTop || 0;
      }
      rafId = requestAnimationFrame(() => process(y));
    };

    // Al girar el telefono o al abrirse el teclado, una pantalla que se
    // desplazaba puede dejar de hacerlo. Si la cabecera se quedo escondida, no
    // habria ningun scroll que la devolviera.
    const alRedimensionar = () => {
      if (!laPaginaSeDesplaza()) setCollapsed(false);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', alRedimensionar);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      window.removeEventListener('resize', alRedimensionar);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  /* ── Escuchar evento para forzar visibilidad ── */
  useEffect(() => {
    const handleForceShow = () => {
      setCollapsed(false);
      ignoreScroll.current = true;
      setTimeout(() => { ignoreScroll.current = false; }, 800); // Ignorar scroll falsos por reacomodo de DOM
    };
    window.addEventListener('header:forceshow', handleForceShow);
    return () => window.removeEventListener('header:forceshow', handleForceShow);
  }, []);

  /* ── Tema ── */
  useEffect(() => {
    if (!forceDark) {
      document.documentElement.setAttribute('data-theme', theme);
      document.body.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    }
  }, [theme, forceDark]);

  const handleToggleTheme = () => {
    if (forceDark) return;
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Ruta RELATIVA, como el resto de la aplicacion. Antes apuntaba a
        // produccion a pelo, asi que cerrar sesion mientras desarrollabas en
        // local iba y revocaba tu token DE PRODUCCION en el servidor de verdad
        // — y el de local se quedaba vivo. El sintoma es desconcertante: te
        // echa de la cuenta buena y te deja dentro de la de pruebas.
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (e) {
        console.warn('Logout remoto falló, limpiando localmente:', e);
      }
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('nsl_sub_data');
    sessionStorage.removeItem('nsl_sub_data_at');
    navigate('/landing', { replace: true });  // '/' ahora es el diagnostico anonimo
  };

  const navItems = [
    // El profesor abre la app para mirar su agenda, no para entrenar: su
    // enlace va el primero. Para los demas la barra no cambia.
    // El centro de mando va el PRIMERO de todos cuando corresponde: quien lo
    // abre no viene a entrenar, viene a mirar el negocio.
    ...(esElJefe
      ? [{ to: RUTA_DEL_CENTRO_DE_MANDO, icon: ShieldCheck, label: 'CRM' }]
      : []),
    ...(esProfesor
      ? [{ to: '/profesor', icon: CalendarCheck, label: 'Clases' }]
      : []),
    // El Coach va el PRIMERO de los del alumno: desde que aterriza en /coach
    // al entrar, es su pantalla de partida y no una sala mas.
    { to: '/coach', icon: Bot, label: 'Coach' },
    { to: '/entrenar', icon: Dumbbell, label: 'Gimnasio' },
    { to: '/home', icon: GraduationCap, label: 'Curso' },
    // El «Chat» del menú no es una pantalla: es el BOTÓN que abre el widget
    // del asistente. A quien no tiene acceso el widget ni se monta, así que
    // sin esta línea el menú le enseñaba un botón muerto —y un icono— de una
    // IA que no le va a contestar. Rob, 17-09-2026: «vamos a protegerlo en su
    // botón y su icono para que no sea un gasto innecesario».
    ...(conChat ? [{ action: 'chat', icon: MessageCircle, label: 'Chat' }] : []),
    { to: '/multigames', icon: Gamepad2, label: 'Juegos' },
    // «Tutor AI» confundia, y el motivo no era el nombre en si: esta sala ya se
    // llama «Situaciones Reales» en el gimnasio y en el catalogo del coach, asi
    // que el menu le daba un SEGUNDO nombre a la misma puerta. Rob, 29-09-2026:
    // «eso de tutor IA genera confusion». Ahora dice lo mismo en los tres
    // sitios; en el menu va en corto porque ahi mandan las etiquetas de una
    // palabra.
    { to: '/tutor', icon: Brain, label: 'Situaciones' },
    { to: '/entrevista', icon: Briefcase, label: 'Entrevista' },
    { to: '/dashboard', icon: BarChart3, label: 'Stats' },
    { to: '/glosario', icon: BookOpen, label: 'Glosario' },
    // Junto a «Planes» a proposito: las dos son decisiones de gastar dinero, y
    // el resto de la barra son herramientas de entrenar. Antes esta pantalla
    // solo la encontraba quien supiera escribir la direccion.
    { to: '/profesores', icon: GraduationCap, label: 'Profesores' },
    { to: '/plans', icon: Zap, label: 'Planes' }, // sin isPremium
  ];

  return (
    <>
      <header className={`sh-site-header${collapsed ? ' sh-collapsed' : ''}`}>
        <div className="sh-header-content">
          <div className="sh-header-left">
            <img
              src={effectiveTheme === 'dark' ? '/logo-dark.png' : '/logo-light.png'}
              alt="Non Stop Learning Logo"
              className="sh-header-logo-img"
            />
          </div>

          <nav className="sh-nav-links">
            {esElJefe && (
              <NavLink to={RUTA_DEL_CENTRO_DE_MANDO} className="sh-mando">
                <ShieldCheck size={15} />
                Command Center
              </NavLink>
            )}
            {esProfesor && (
              <NavLink to="/profesor">Mis clases</NavLink>
            )}
            <NavLink to="/coach">Coach</NavLink>
            <NavLink to="/entrenar">Gimnasio</NavLink>
            <NavLink to="/home">Curso</NavLink>
            {/* Aqui NO va el boton de Chat, y es a proposito.
                Rob, 28-09-2026: «en pantallas grandes ponemos el boton de chat
                (...) lo veo mal que este ahi porque ocupa espacio; en celulares
                es perfecto porque no manejamos el icono flotante».
                En escritorio el widget ya tiene su burbuja flotante, asi que el
                enlace del menu era una segunda puerta a la misma sala, pagada
                con el sitio que necesitan los enlaces que no tienen otra. En la
                barra del movil sigue estando —ahi es la unica puerta— y se
                monta desde `navItems`. */}
            <NavLink to="/multigames">Mini Games</NavLink>
            <NavLink to="/tutor">Situaciones</NavLink>
            <NavLink to="/entrevista">Entrevista</NavLink>
            <NavLink to="/dashboard">Dashboard</NavLink>
            <NavLink to="/glosario">Glosario</NavLink>
            {/* Va también aquí, y no sólo en `navItems`: esta lista es la del
                ESCRITORIO y se escribe a mano, así que añadir la entrada arriba
                sólo la ponía en la barra del móvil. Un alumno en un ordenador
                no habría visto nunca la vitrina. */}
            <NavLink to="/profesores">Profesores</NavLink>
            <NavLink to="/plans">Planes</NavLink> {/* sin clase premium */}
          </nav>

          <div className="sh-header-right">
            <InstallPWA />
            <button
              className="sh-theme-toggle"
              onClick={handleToggleTheme}
              title={forceDark ? 'Tema fijado' : 'Toggle Theme'}
              style={{ opacity: forceDark ? 0.5 : 1, cursor: forceDark ? 'not-allowed' : 'pointer' }}
            >
              {effectiveTheme === 'light' ? '🌙' : '☀️'}
            </button>
            {user ? (
              <UserStatusDropdown user={user} onLogout={handleLogout} />
            ) : (
              <NavLink to="/login" className="sh-auth-button">Login</NavLink>
            )}
          </div>
        </div>
      </header>

      <nav
        className={`sh-mobile-bottom-nav${collapsed ? ' sh-nav-collapsed' : ''}`}
        aria-label="Navegación principal"
      >
        <button
          className={`sh-nav-arrow sh-nav-arrow-left${showLeft ? ' sh-arrow-show' : ''}`}
          onClick={() => scrollRef.current?.scrollBy({ left: -100, behavior: 'smooth' })}
          aria-hidden="true"
          tabIndex={-1}
        >
          <span className="sh-arrow-chevron">‹</span>
        </button>

        <div className="sh-nav-scroll" ref={scrollRef} role="list">
          {navItems.map((item) => {
            const Icon = item.icon;
            
            if (item.action === 'chat') {
              return (
                <button
                  key="chat-action"
                  className="sh-mobile-nav-item"
                  onClick={() => window.dispatchEvent(new Event('omniwidget:toggle'))}
                  aria-label="Chat con NSL"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <Icon className="sh-mobile-nav-icon" aria-hidden="true" />
                  <span className="sh-mobile-nav-label">{item.label}</span>
                </button>
              );
            }

            return (
              <NavLink
                key={item.to}
                to={item.to}
                role="listitem"
                className={({ isActive }) =>
                  `sh-mobile-nav-item${isActive ? ' active' : ''}` // sin sh-premium-item
                }
                aria-label={item.label}
              >
                <Icon className="sh-mobile-nav-icon" aria-hidden="true" />
                <span className="sh-mobile-nav-label">{item.label}</span>
              </NavLink>
            );
          })}
        </div>

        <button
          className={`sh-nav-arrow sh-nav-arrow-right${showRight ? ' sh-arrow-show' : ''}`}
          onClick={() => scrollRef.current?.scrollBy({ left: 100, behavior: 'smooth' })}
          aria-hidden="true"
          tabIndex={-1}
        >
          <span className="sh-arrow-chevron">›</span>
        </button>
      </nav>

      {/* 💬 WIDGET V2 — Renderizado globalmente para que esté en todas las páginas.
          MENOS a quien no tiene acceso. Rob, 17-09-2026: «sigue saliendo el chat
          y como manejamos un asistente de IA puede ser contraproducente, porque
          es de uso de IA de la empresa y al no tener un control no debería
          salir». No se esconde con CSS: NO SE MONTA, así que tampoco sondea el
          servidor cada 3 segundos. El candado de verdad está en el backend
          (`require_plan_vigente` en las rutas del chat y del asistente); esto es
          la cortesía de no enseñar un botón que no va a funcionar. */}
      {user && conChat && <OmniWidgetV2 />}
    </>
  );
}

export default Header;